/**
 * This browser's own say on the chat, on top of the server's list. By default the chat shows to
 * the people the server answers (GET /assistant/access); the link overrides that here:
 *
 *   ?xat=si  → this browser shows the chat, whatever the list says
 *   ?xat=no  → this browser hides it, whatever the list says
 *
 * The same shape as `serverMenuOptIn`: remembered in localStorage so it survives reloads and
 * in-app navigation, and simply does not stick when storage is unavailable (private mode, blocked
 * site data) — in which case the list decides.
 *
 * This only decides whether the app OFFERS the chat. Whether the server answers is its own switch
 * and list (`ASSISTANT_ENABLED`, `ASSISTANT_ALLOWED_USER_IDS`), checked on every turn.
 */
const STORAGE_KEY = "thinkeat.assistant";
const QUERY_PARAM = "xat";
const ON = "si";
const OFF = "no";

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage blocked: the opt-in will not persist and the chat stays hidden.
  }
}

/**
 * Applies `?xat=` if present. HashRouter keeps routes after `#`, so the parameter can sit in
 * either the normal query string or the one inside the hash. Read in `main.tsx` before the router
 * rewrites the URL.
 */
export function applyAssistantOptInFromUrl(
  location: Pick<Location, "search" | "hash"> = window.location,
): void {
  const fromSearch = new URLSearchParams(location.search).get(QUERY_PARAM);
  const hashQuery = location.hash.includes("?") ? location.hash.slice(location.hash.indexOf("?")) : "";
  const fromHash = new URLSearchParams(hashQuery).get(QUERY_PARAM);
  const requested = fromSearch ?? fromHash;

  if (requested === ON) writeStored(ON);
  else if (requested === OFF) writeStored(OFF);
}

/** What this browser chose with the link: "on", "off", or null to leave it to the server's list. */
export function assistantBrowserChoice(): "on" | "off" | null {
  const stored = readStored();
  if (stored === ON) return "on";
  if (stored === OFF) return "off";
  return null;
}

/** True when this browser has opted in to the chat. */
export function usesAssistant(): boolean {
  return assistantBrowserChoice() === "on";
}
